import { DEFAULT_POST_PRODUCTION_COMPANY_ID } from './../utils/constants';
import { getLoggedUser } from './../utils/helpers/tokenStorage';
import { TOAST_DURATION } from './../utils/helpers/constants';
import { CampaignWizardMode } from './../models/enums';
import { toast } from "react-toastify";
import ExistingCampaignPrompt from "../popups/ExistingCampaignPrompt";
import { toggleLoading } from "../store/slices/appSlice";
import { RoutesE } from '../routes/routesData';
import { trimObjectFields } from '../utils/helpers/trimObjectFields';
import { DEFAULT_PRODUCTION_COMPANY_ID, MEDIA_COMPANY_NONE_ID } from '../utils/constants';
import getDefaultAdAgencyId from '../popups/CampaignWizard/helpers/getDefaultAdAgencyId';

/**
 * Uses when you want to create NEW campaign (or create new from COPY of existing one)
*/

export const handleCreateCampaign = async (
    campaignData: any,
    addToExist: string,
    campaignRetry: boolean,
    createPopup: (config: any) => void,
    removePopup: () => void,
    removeAllPopups: () => void,
    createProject: (body: any) => void,
    dispatch: (action: any) => void,
    navigate: (action: any) => void,
    fetchAllProjects: (action: any) => void,
    searchParams: any,
    mode: CampaignWizardMode,
) => {
    const {user} = getLoggedUser();
    const body = {
        ...campaignData,
        BrandId: campaignData.BrandId.value,
        IndustryId: campaignData.IndustryId.value,
        AdvertiserId: campaignData.AdvertiserId.value,
        // "Self Media Buy" (sentinel) persists as no FK (null); a real selection sends its client id.
        // Mirrors mapCampaignDataForSubmitting.ts so both wizards write MediaCompanyId identically.
        MediaCompanyId: campaignData.MediaCompanyId?.value === MEDIA_COMPANY_NONE_ID ? null : (campaignData.MediaCompanyId?.value ?? null),
        ProductionLocationIsrael: campaignData.ProductionLocationIsrael === 'local',
        PostProductionCompanyId: campaignData.PostProductionCompanyId?.value || DEFAULT_POST_PRODUCTION_COMPANY_ID,
        ProductionCompanyId: String(campaignData.ProductionCompanyId?.value || DEFAULT_PRODUCTION_COMPANY_ID),
        AdvertisingAgencyId: getDefaultAdAgencyId(user?.user),
        AdvertisingAgencyName: campaignData.AdvertisingAgencyId.label,
        ParticipantNames: campaignData.ParticipantNames || (user?.user?.culture === 'he-IL' ? 'אין' : 'NONE'),
        SpeakerNames: campaignData.SpeakerNames || (user?.user?.culture === 'he-IL' ? 'אין' : 'NONE'),
        ProjectType: "VideoAd",
        ProjectComment: campaignData.Name || null,
        IsCopyOperation: mode === CampaignWizardMode.COPY,
        Campaign: {
            Id: '',
            CampaignId: campaignData?.campaignId,
        },
        BroadcasterIds: campaignData.selectedChannels.join(','),
        broadcasters: [],
        addToExist,
        campaignRetry: +addToExist ? true : campaignRetry,
    };

    delete body.selectedChannels;
    delete body.campaignId;

    const res: any = await createProject(trimObjectFields(body));

    if (res.error && res.error.status !== 302) {
        return
    }

    const retryMutation = async (addToExist: string, campaignRetry: boolean) => {
        await handleCreateCampaign(
            { ...campaignData, campaignId: res?.error?.data?.[0].campaignId },
            addToExist,
            campaignRetry,
            createPopup,
            removePopup,
            removeAllPopups,
            createProject,
            dispatch,
            navigate,
            fetchAllProjects,
            searchParams,
            mode,
        );
    };

    if (Array.isArray(res?.error?.data)) {
        // spec-47 prompt. This legacy one-step wizard posts a REAL delivery (no IsCampaignCreation), so
        // "use existing" re-submits with addToExist = the campaign — that creates the delivery under it,
        // and the success branch below closes every popup. Unlike the campaign wizard, nothing to navigate to.
        createPopup(ExistingCampaignPrompt({
            campaigns: res.error.data,
            onUseExisting: (campaignId: number) => retryMutation(String(campaignId), true),
            onCreateNew: () => retryMutation('0', true),
        }));
    } else {
        dispatch(toggleLoading());
        dispatch(toggleLoading());
        removeAllPopups();
        toast('Successfully created', {
            type: 'success',
            autoClose: TOAST_DURATION,
        });
        const path = window.location.pathname;

        if (path.toLowerCase().includes('deliveryserviceindex') || path.toLowerCase().includes('confirmation')) {
            navigate(`/deliveries/${res.data.Id}`);
        } else {
            navigate(RoutesE.PROJECTS);
        }
    }
};